import {
    Avatar,
    ListItem,
    ListItemAvatar,
    ListItemText,
    IconButton,
    CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import moment from "moment";
import "moment/locale/fr";
import { withSnackbar } from "../../utils/SnackbarHOC";
import { updateSeenNotification, deleteNotification } from "../../services/NotificationService";
import { FaTasks } from "react-icons/fa";
import { MdInfoOutline, MdUpdate, MdNotifications, MdDelete } from "react-icons/md";
import { useState } from "react";

moment.locale("fr");

const MESSAGE_BY_TYPE = {
    1: (n) =>
        `Nouvelle tâche assignée pour le candidat : ${n.candidatName ?? "—"}`,
    2: (n) =>
        `Nouvelle demande ${n.candidatName ?? ""}`.trim() ||
        "Mise à jour (type 2)",
    99: (n) => n.title || "Nouveaux profils dans le vivier !",
};

const LINK_BY_TYPE = {
    1: (n) => `/candidats/${n.candidat}/view`,
    2: (n) => (`/demandes/`),
    99: () => "/candidats-offre",
};

const ICON_BY_TYPE = {
    1: FaTasks,
    2: MdUpdate,
    99: MdNotifications,
};

function GenericNotification({ notif, snackbarShowMessage, onUpdate, onDelete }) {
    const [isDeleting, setIsDeleting] = useState(false);
    const handleClick = async () => {
        if (notif.type === 99 && notif.candidates) {
            sessionStorage.setItem('co_rawCandidats', JSON.stringify(notif.candidates));
            sessionStorage.setItem('co_jobUuid', notif.mission_id);
            sessionStorage.setItem('co_mission', JSON.stringify({
                id: notif.mission_id,
                value: notif.title,
                nom_societe: notif.nom_societe || null,
            }));
        }
        if (!notif.seen) {
            try {
                await updateSeenNotification(notif.uuid);
                onUpdate({ ...notif, seen: true });
            } catch (error) {
                snackbarShowMessage(
                    "Erreur lors de la mise à jour de la notification",
                    "error",
                    2000
                );
            }
        }
    };
    const handleDelete = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (isDeleting) return;

        setIsDeleting(true);
        try {
            await deleteNotification(notif.uuid);
            onDelete(notif.uuid);
        } catch (error) {
            setIsDeleting(false);
            snackbarShowMessage(
                "Erreur lors de la suppression de la notification",
                "error",
                2000
            );
        }
    };

    const primary =
        (MESSAGE_BY_TYPE[notif.type] || (() => "Notification"))(notif);
    const to = (LINK_BY_TYPE[notif.type] || (() => "#"))(notif);
    const Icon = ICON_BY_TYPE[notif.type] || MdInfoOutline;

    const timeAgo = (d) => moment(d).fromNow();

    return (
        <ListItem
            key={notif.uuid}
            dense
            selected={!notif.seen}
            divider
            disablePadding
            sx={{
                opacity: isDeleting ? 0.5 : 1,
                transition: "opacity 0.2s ease",
            }}
        >
            <ListItem
                button
                component={Link}
                to={{ pathname: to }}
                onClick={handleClick}
                sx={{ flex: 1, pointerEvents: isDeleting ? "none" : "auto" }}
            >
                <ListItemAvatar>
                    <Avatar>
                        <Icon />
                    </Avatar>
                </ListItemAvatar>

                <ListItemText primary={primary} secondary={notif.message} />
                <ListItemText
                    primaryTypographyProps={{ fontSize: "0.6875rem", textAlign: "right" }}
                    primary={timeAgo(notif.createdAt)}
                />
            </ListItem>

            {notif.type === 99 && (
                isDeleting ? (
                    <CircularProgress size={20} sx={{ mx: 1 }} />
                ) : (
                    <IconButton
                        size="small"
                        type="button"
                        onClick={handleDelete}
                        aria-label="supprimer"
                    >
                        <MdDelete />
                    </IconButton>
                )
            )}
        </ListItem>
    );
}

GenericNotification.propTypes = {
    notif: PropTypes.shape({
        uuid: PropTypes.string.isRequired,
        type: PropTypes.number.isRequired,
        seen: PropTypes.bool.isRequired,
        createdAt: PropTypes.string.isRequired,
        candidat: PropTypes.string,
        candidatName: PropTypes.string,
        demande: PropTypes.string,
        demandeRef: PropTypes.string,
    }).isRequired,
    onUpdate: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    snackbarShowMessage: PropTypes.func.isRequired,
};

export default withSnackbar(GenericNotification);
